import {useIntl} from 'react-intl'
import {formatPhoneNumber} from '@salesforce/retail-react-app/app/utils/phone-utils'

export default function useProfileFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_profile_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_profile_fields.error.required_first_name'
                }),
                validate: (value) => {
                    const regex = /^[a-zA-Z0-9\s'-]+$/
                    if (!regex.test(value)) {
                        return formatMessage({
                            defaultMessage: 'Special characters are not allowed in first name.',
                            id: 'use_profile_fields.error.invalid_first_name_characters'
                        })
                    }
                    return true
                }
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_profile_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_profile_fields.error.required_last_name'
                }),
                validate: (value) => {
                    const regex = /^[a-zA-Z0-9\s'-]+$/
                    if (!regex.test(value)) {
                        return formatMessage({
                            defaultMessage: 'Special characters are not allowed in last name.',
                            id: 'use_profile_fields.error.invalid_last_name_characters'
                        })
                    }
                    return true
                }
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({defaultMessage: 'Email', id: 'use_profile_fields.label.email'}),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_profile_fields.error.required_email'
                }),
                pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid email address format.',
                        id: 'use_registration_fields.error.invalid_email'
                    })
                }
            },
            error: errors[`${prefix}email`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone',
                id: 'use_profile_fields.text_phone'
            }),
            defaultValue: '',
            type: 'phone',
            autoComplete: 'phone',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_profile_fields.error.required_phone'
                }),
                validate: (value) => {
                    const digitsOnly = value.replace(/\D/g, '')
                    if (!/^[2-9]\d{2}[2-9](?!11)\d{6}$/.test(digitsOnly)) {
                        return formatMessage({
                            defaultMessage: 'Please enter a valid 10-digit phone number.',
                            id: 'use_registration_fields.error.invalid_phone'
                        })
                    }
                    return true
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value))
                }
            }),
            control
        },
        birthday: {
            name: `${prefix}birthday`,
            label: formatMessage({
                defaultMessage: 'Birthday (Optional)',
                id: 'use_registration_fields.label.birthday'
            }),
            placeholder: '+1',
            type: 'date',
            autoComplete: 'tel',
            defaultValue: '',
            rules: {
                validate: (value) => {
                    if (!value) return true
                    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
                    if (!match) {
                        return formatMessage({
                            defaultMessage: 'Please enter a valid birthday.',
                            id: 'use_registration_fields.error.invalid_format_date_new'
                        })
                    }

                    const year = parseInt(match[1], 10)
                    const month = parseInt(match[2], 10)
                    const day = parseInt(match[3], 10)

                    if (year < 1900 || year > 2099) {
                        return formatMessage({
                            defaultMessage: 'Year must be between 1900 and 2099.',
                            id: 'use_registration_fields.error.invalid_year'
                        })
                    }

                    // Basic calendar sanity check
                    if (month > 12 || day > 31) {
                        return formatMessage({
                            defaultMessage: 'Invalid month or day.',
                            id: 'use_registration_fields.error.invalid_date'
                        })
                    }

                    const enteredDate = new Date(value)
                    const today = new Date()

                    if (enteredDate.getTime() > today.getTime()) {
                        return formatMessage({
                            defaultMessage: 'Birthday cannot be in the future.',
                            id: 'use_registration_fields.error.future_date_not_allowed_future'
                        })
                    }

                    return true
                }
            },
            error: errors[`${prefix}birthday`],
            control
        }
    }

    return fields
}
